import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { IconAlert, IconLoader } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

type Labels = Record<string, string>;

interface MetaOption {
  value: string;
  labels: Labels;
}
interface MetaColumn {
  key: string;
  kind: string;
  labels: Labels;
}
interface MetaFilter {
  key: string;
  kind: "multi" | "select" | "bool";
  labels: Labels;
  options: MetaOption[];
}
interface MetaDataset {
  key: string;
  labels: Labels;
  period_mode: "range" | "point" | "none";
  columns: MetaColumn[];
  granularity_options: MetaOption[] | null;
  granularity_columns: Record<string, MetaColumn[]> | null;
  filters: MetaFilter[];
}
interface Meta {
  datasets: MetaDataset[];
  formats: string[];
}
interface ExportJob {
  id: string;
  dataset: string;
  format: string;
  status: "pending" | "processing" | "done" | "failed";
  file_name: string | null;
  row_count: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}
interface ExportJobPage {
  items: ExportJob[];
  total: number;
}

type Preset =
  | "today" | "yesterday" | "thisWeek" | "last7" | "lastWeek"
  | "thisMonth" | "last30" | "lastMonth" | "thisQuarter" | "lastQuarter"
  | "thisYear" | "lastYear" | "last24" | "custom";

const PRESETS: Preset[] = [
  "today", "yesterday", "thisWeek", "last7", "lastWeek",
  "thisMonth", "last30", "lastMonth", "thisQuarter", "lastQuarter",
  "thisYear", "lastYear", "last24", "custom",
];

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function presetRange(preset: Exclude<Preset, "custom">): [Date, Date] {
  const now = new Date();
  const end = now;
  const start = new Date(now);
  const quarterStart = (d: Date) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
  switch (preset) {
    case "today":
      start.setHours(0, 0, 0, 0);
      break;
    case "yesterday": {
      const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const e = new Date(s); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "last24":
      start.setTime(now.getTime() - 24 * 3600 * 1000);
      break;
    case "thisWeek": {
      const dow = (now.getDay() + 6) % 7; // Monday = 0
      start.setDate(now.getDate() - dow);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case "lastWeek": {
      const dow = (now.getDay() + 6) % 7;
      const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow - 7);
      const e = new Date(s); e.setDate(e.getDate() + 6); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case "last7":
      start.setTime(now.getTime() - 7 * 24 * 3600 * 1000);
      break;
    case "thisMonth":
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case "last30":
      start.setTime(now.getTime() - 30 * 24 * 3600 * 1000);
      break;
    case "lastMonth": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return [s, e];
    }
    case "thisQuarter":
      return [quarterStart(now), end];
    case "lastQuarter": {
      const s = quarterStart(now);
      const prevStart = new Date(s.getFullYear(), s.getMonth() - 3, 1);
      const prevEnd = new Date(s.getFullYear(), s.getMonth(), 0, 23, 59, 59);
      return [prevStart, prevEnd];
    }
    case "thisYear":
      return [new Date(now.getFullYear(), 0, 1), end];
    case "lastYear":
      return [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59)];
  }
  return [start, end];
}

const FORMATS: { key: string; ext: string }[] = [
  { key: "csv", ext: ".csv" },
  { key: "xlsx", ext: ".xlsx" },
  { key: "pdf", ext: ".pdf" },
];

export default function Exports() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "cs").slice(0, 2);
  const L = useCallback((labels: Labels | undefined) => (labels ? labels[lang] || labels["cs"] : ""), [lang]);

  const [meta, setMeta] = useState<Meta | null>(null);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageIndex, setPageIndex] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const [datasetKey, setDatasetKey] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<string>("");
  const [preset, setPreset] = useState<Preset>("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [at, setAt] = useState(() => toLocalInput(new Date()));
  const [selectedCols, setSelectedCols] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [format, setFormat] = useState("csv");
  const [csvDialect, setCsvDialect] = useState<"cs" | "rfc">("cs");
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef<number | null>(null);

  const dataset = useMemo(
    () => meta?.datasets.find((d) => d.key === datasetKey) ?? null,
    [meta, datasetKey],
  );
  const columns = useMemo(() => {
    if (!dataset) return [];
    if (granularity && dataset.granularity_columns?.[granularity]) {
      return dataset.granularity_columns[granularity];
    }
    return dataset.columns;
  }, [dataset, granularity]);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get<ExportJobPage>(`/exports?limit=${pageSize}&offset=${pageIndex * pageSize}`);
      setJobs(data.items);
      setJobsTotal(data.total);
      return data.items;
    } catch {
      return [] as ExportJob[];
    }
  }, [pageSize, pageIndex]);

  // Poll while any job is unfinished.
  useEffect(() => {
    const pending = jobs.some((j) => j.status === "pending" || j.status === "processing");
    if (pending && pollRef.current === null) {
      pollRef.current = window.setInterval(async () => {
        const data = await loadJobs();
        if (!data.some((j) => j.status === "pending" || j.status === "processing") && pollRef.current !== null) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 2500);
    }
    if (!pending && pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [jobs, loadJobs]);
  useEffect(() => () => { if (pollRef.current !== null) window.clearInterval(pollRef.current); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [m] = await Promise.all([
          api.get<Meta>("/exports/meta").then((r) => r.data),
          loadJobs(),
        ]);
        setMeta(m);
      } catch (e) {
        setErr(apiError(e, t("exports.loadError")));
      }
    })();
  }, [loadJobs, t]);

  function pickDataset(key: string) {
    setDatasetKey(key);
    const ds = meta?.datasets.find((d) => d.key === key) ?? null;
    const gran = ds?.granularity_options?.[0]?.value ?? "";
    setGranularity(gran);
    const cols = gran && ds?.granularity_columns?.[gran] ? ds.granularity_columns[gran] : ds?.columns ?? [];
    setSelectedCols(new Set(cols.map((c) => c.key)));
    setFilters({});
    const now = new Date();
    const [s, e] = presetRange("today");
    setPreset("today");
    setDateFrom(toLocalInput(s));
    setDateTo(toLocalInput(e));
    setAt(toLocalInput(now));
  }

  function applyPreset(p: Preset) {
    setPreset(p);
    if (p === "custom") return;
    const [s, e] = presetRange(p);
    setDateFrom(toLocalInput(s));
    setDateTo(toLocalInput(e));
  }

  function toggleCol(key: string) {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function setFilter(key: string, value: unknown) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function toggleMulti(key: string, value: string) {
    setFilters((prev) => {
      const cur = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: next };
    });
  }

  // Rough table-width estimate for the PDF warning — mirrors the auto-scale
  // heuristic in formats.py (kind minimums vs. label length, char units).
  const KIND_MIN_CHARS: Record<string, number> = {
    text: 12, money: 12, int: 8, number: 10, percent: 7, datetime: 16, bool: 6,
  };
  const PDF_WARN_CHARS = 220;
  const estPdfChars = useMemo(
    () => columns
      .filter((c) => selectedCols.has(c.key))
      .reduce((s, c) => s + Math.max(L(c.labels).length, KIND_MIN_CHARS[c.kind] ?? 12) + 2, 0),
    [columns, selectedCols, L],
  );
  const pdfTooWide = format === "pdf" && estPdfChars > PDF_WARN_CHARS;

  const canNext1 = !!dataset && (
    dataset.period_mode === "none"
    || (dataset.period_mode === "point" && !!at)
    || (dataset.period_mode === "range" && !!dateFrom && !!dateTo)
  );
  const canNext2 = selectedCols.size > 0;

  async function submit() {
    if (!dataset) return;
    setSubmitting(true);
    setErr(null);
    try {
      const params: Record<string, unknown> = {
        columns: columns.filter((c) => selectedCols.has(c.key)).map((c) => c.key),
        filters,
        lang,
      };
      if (dataset.period_mode === "range") {
        params.date_from = new Date(dateFrom).toISOString();
        params.date_to = new Date(dateTo).toISOString();
      }
      if (dataset.period_mode === "point") {
        params.at = new Date(at).toISOString();
      }
      if (granularity) params.granularity = granularity;
      if (format === "csv") params.csv_dialect = csvDialect;
      await api.post("/exports", { dataset: dataset.key, format, params });
      await loadJobs();
      setStep(1);
      setDatasetKey(null);
    } catch (e) {
      setErr(apiError(e, t("exports.createError")));
    } finally {
      setSubmitting(false);
    }
  }

  async function download(job: ExportJob) {
    try {
      const { data } = await api.get(`/exports/${job.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.file_name || `export.${job.format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(apiError(e, t("exports.downloadError")));
    }
  }

  const pageCount = Math.max(1, Math.ceil(jobsTotal / pageSize));

  if (!meta) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  }

  const jobStatusBadge = (s: string) =>
    s === "done" ? "badge badge-green" : s === "failed" ? "badge badge-red" : "badge badge-yellow";

  return (
    <div>
      <div className="page-header">
        <h1>{t("exports.title")}</h1>
        <p>{t("exports.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      {/* ---- Wizard ---- */}
      <div className="card" style={{ padding: 24, marginBottom: 28 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 20, fontSize: 13, color: "var(--muted)" }}>
          {[1, 2, 3].map((s) => (
            <span key={s} style={{
              padding: "4px 12px", borderRadius: 20, fontWeight: step === s ? 700 : 400,
              background: step === s ? "var(--accent-dim)" : "transparent",
              color: step === s ? "var(--accent)" : "var(--muted)",
              border: "1px solid var(--border)",
            }}>
              {s}. {t(`exports.step${s}`)}
            </span>
          ))}
        </div>

        {step === 1 && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
              {meta.datasets.map((d) => (
                <button
                  key={d.key}
                  onClick={() => pickDataset(d.key)}
                  style={{
                    textAlign: "left", padding: "14px 16px", borderRadius: "var(--radius-sm)",
                    border: `2px solid ${datasetKey === d.key ? "var(--accent)" : "var(--border)"}`,
                    background: datasetKey === d.key ? "var(--accent-dim)" : "var(--bg-elevated)",
                    cursor: "pointer", color: "var(--text)", fontWeight: 600, fontSize: 14,
                  }}
                >
                  {L(d.labels)}
                </button>
              ))}
            </div>

            {dataset?.period_mode === "range" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      className={preset === p ? "primary" : "ghost"}
                      style={{ padding: "6px 12px", fontSize: 13 }}
                      onClick={() => applyPreset(p)}
                    >
                      {t(`exports.presets.${p}`)}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13, color: "var(--muted)" }}>
                    {t("exports.dateFrom")}{" "}
                    <input type="datetime-local" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }} />
                  </label>
                  <label style={{ fontSize: 13, color: "var(--muted)" }}>
                    {t("exports.dateTo")}{" "}
                    <input type="datetime-local" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }} />
                  </label>
                </div>
              </div>
            )}

            {dataset?.period_mode === "point" && (
              <label style={{ fontSize: 13, color: "var(--muted)" }}>
                {t("exports.atTime")}{" "}
                <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
              </label>
            )}
          </>
        )}

        {step === 2 && dataset && (
          <>
            {dataset.granularity_options && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{t("exports.granularity")}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {dataset.granularity_options.map((g) => (
                    <button
                      key={g.value}
                      className={granularity === g.value ? "primary" : "ghost"}
                      style={{ padding: "6px 14px", fontSize: 13 }}
                      onClick={() => {
                        setGranularity(g.value);
                        const cols = dataset.granularity_columns?.[g.value] ?? dataset.columns;
                        setSelectedCols(new Set(cols.map((c) => c.key)));
                      }}
                    >
                      {L(g.labels)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
              {t("exports.columns")}{" "}
              <button className="ghost" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => setSelectedCols(new Set(columns.map((c) => c.key)))}>{t("exports.all")}</button>
              <button className="ghost" style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => setSelectedCols(new Set())}>{t("exports.none")}</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 6, marginBottom: 24 }}>
              {columns.map((c) => (
                <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                  <input type="checkbox" checked={selectedCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                  {L(c.labels)}
                </label>
              ))}
            </div>

            {dataset.filters.length > 0 && (
              <>
                <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{t("exports.filters")}</div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {dataset.filters.map((f) => (
                    <div key={f.key} style={{ minWidth: 220 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{L(f.labels)}</div>
                      {f.kind === "multi" ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {f.options.map((o) => (
                            <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={Array.isArray(filters[f.key]) && (filters[f.key] as string[]).includes(o.value)}
                                onChange={() => toggleMulti(f.key, o.value)}
                              />
                              {L(o.labels)}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <SearchableSelect
                          value={typeof filters[f.key] === "string" ? (filters[f.key] as string) : "all"}
                          onChange={(v) => setFilter(f.key, v === "all" ? undefined : v)}
                          options={[{ value: "all", label: t("exports.allOptions") }, ...f.options.map((o) => ({ value: o.value, label: L(o.labels) }))]}
                          placeholder={t("exports.allOptions")}
                          searchPlaceholder={t("exports.searchOption")}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>{t("exports.format")}</div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {FORMATS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFormat(f.key)}
                  style={{
                    padding: "14px 22px", borderRadius: "var(--radius-sm)", fontWeight: 700, fontSize: 14,
                    border: `2px solid ${format === f.key ? "var(--accent)" : "var(--border)"}`,
                    background: format === f.key ? "var(--accent-dim)" : "var(--bg-elevated)",
                    cursor: "pointer", color: "var(--text)",
                  }}
                >
                  {f.key.toUpperCase()} <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 12 }}>{f.ext}</span>
                </button>
              ))}
            </div>
            {pdfTooWide && (
              <div className="card" style={{ padding: "12px 16px", marginBottom: 16, background: "var(--yellow-dim, rgba(234,179,8,0.12))", color: "var(--text)", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <IconAlert size={16} /> {t("exports.pdfTooWide")}
              </div>
            )}
            {format === "csv" && (
              <label style={{ fontSize: 13, color: "var(--muted)", display: "block" }}>
                {t("exports.csvDialect")}{" "}
                <select value={csvDialect} onChange={(e) => setCsvDialect(e.target.value as "cs" | "rfc")} style={{ width: "auto" }}>
                  <option value="cs">{t("exports.csvCs")}</option>
                  <option value="rfc">{t("exports.csvRfc")}</option>
                </select>
              </label>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
          <button className="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
            ‹ {t("exports.back")}
          </button>
          {step < 3 ? (
            <button
              className="primary"
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              onClick={() => setStep((s) => s + 1)}
            >
              {t("exports.next")} ›
            </button>
          ) : (
            <button className="primary" disabled={submitting} onClick={submit}>
              {submitting ? t("exports.creating") : t("exports.create")}
            </button>
          )}
        </div>
      </div>

      {/* ---- History ---- */}
      <div className="card">
        <h3 style={{ padding: "16px 16px 0", fontSize: 16, fontWeight: 700 }}>{t("exports.history")}</h3>
        {jobs.length === 0 ? (
          <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("exports.empty")}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t("exports.colCreated")}</th>
                <th>{t("exports.colDataset")}</th>
                <th>{t("exports.colFormat")}</th>
                <th>{t("exports.colRows")}</th>
                <th>{t("exports.colStatus")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td style={{ color: "var(--muted)", fontSize: 13 }}>{new Date(j.created_at).toLocaleString(lang === "cs" ? "cs-CZ" : lang === "sk" ? "sk-SK" : "en-GB")}</td>
                  <td style={{ fontWeight: 600 }}>{L(meta.datasets.find((d) => d.key === j.dataset)?.labels) || j.dataset}</td>
                  <td className="mono">{j.format.toUpperCase()}</td>
                  <td>{j.row_count ?? "—"}</td>
                  <td>
                    <span className={jobStatusBadge(j.status)}>
                      {t(`exports.status.${j.status}`, { defaultValue: j.status })}
                    </span>
                    {j.error && <span style={{ color: "var(--red)", fontSize: 12, marginLeft: 8 }}>{j.error}</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {j.status === "done" && (
                      <button className="ghost" style={{ fontSize: 13 }} onClick={() => download(j)}>
                        {t("exports.download")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {jobs.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
              <span>{t("exports.perPage")}</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageIndex(0); }} style={{ width: "auto" }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>· {t("exports.totalCount", { total: jobsTotal })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <button className="ghost" disabled={pageIndex === 0} onClick={() => setPageIndex((i) => i - 1)}>‹ {t("exports.prev")}</button>
              <span style={{ color: "var(--muted)" }}>{pageIndex + 1} / {pageCount}</span>
              <button className="ghost" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex((i) => i + 1)}>{t("exports.next")} ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
